import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { IUserJson } from '@scale/shared/types/user';
import {
  AuditPermissionRole,
  GlobalWorkerSkillScorePermission,
} from '@scale/shared/taskerScreening/types';
import API from '@scale/frontend-lib/dashboard-api';
import { CircularProgress } from 'react-scale/components/design-system';
import { Button } from '@scale/scaleui';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import Table from 'corp/components/UI/Table';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import TaskerScreeningAuditorPermissionReviewModal from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningAuditorPermissionReviewModal';
import TaskerScreeningAuditorPermissionBenchmarkModal from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningAuditorPermissionBenchmarkModal';
import TaskerScreeningAuditorPermissionFilterModal from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningAuditorPermissionFilterModal';
import TaskerScreeningAuditPermissionRoleCreationModal from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningAuditPermissionRoleCreationModal';
import TaskerScreeningAuditPermissionRoleArchiveModal from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningAuditPermissionRoleArchiveModal';

interface TaskerScreeningAuditorPermissionsToolProps {
  user: IUserJson;
  needsFetch: boolean;
  setNeedsFetch: (needsFetch: boolean) => void;
}

const tableHeaders: Record<string, string> = {
  id: 'ID',
  name: 'Name',
  description: 'Description',
  edit: 'Edit',
  reviewAndAssign: 'Workers',
  reviewBenchmarks: 'Benchmarks',
  reviewFilters: 'Filters',
  courseIds: 'Course IDs',
  sectionTitles: 'Section Titles',
  createdBy: 'Created By',
  numWorkersPermissioned: 'Workers Permissioned',
  numBenchmarksAssigned: 'Benchmarks Assigned',
};

const shownTableHeaders = [
  'name',
  'edit',
  'reviewAndAssign',
  'reviewBenchmarks',
  'reviewFilters',
  'courseIds',
  'sectionTitles',
  'createdBy',
  'numWorkersPermissioned',
  'numBenchmarksAssigned',
];

const filterableTableHeaders = [
  'name',
  'courseIds',
  'sectionTitles',
  'numWorkersPermissioned',
  'numBenchmarksAssigned',
];

const smallColumns = [
  'enabled',
  'edit',
  'numWorkersPermissioned',
  'numBenchmarksAssigned',
  'createdBy',
];

const TaskerScreeningAuditorPermissionsTool: React.FC<
  TaskerScreeningAuditorPermissionsToolProps
> = ({ needsFetch, setNeedsFetch }) => {
  const [permissions, setPermissions] = useState<AuditPermissionRole[]>([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [openWorkerModal, setOpenWorkerModal] = useState(false);
  const [openBenchmarksModal, setOpenBenchmarksModal] = useState(false);
  const [openFiltersModal, setOpenFiltersModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openForkModal, setOpenForkModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [activePermission, setActivePermission] = useState<AuditPermissionRole>();

  const handleFetchPermissions = useCallback(async () => {
    setLoadingTable(true);
    const { permissions } = await API.genai.fetchAuditPermissions({});
    setPermissions(permissions);
    setNeedsFetch(false);
    setLoadingTable(false);
  }, [setNeedsFetch]);

  const updateAuditorPermissions = async (
    id: string,
    addWorkers: string[],
    removeWorkers: string[],
    disableWorkers: string[],
  ) => {
    await API.genai.updateAuditorPermissions({
      id,
      addWorkers,
      removeWorkers,
      disableWorkers,
    });
  };

  const handleUpdateAuditorPermissions = useCallback(
    async (id: string, addWorkers: string[], removeWorkers: string[], disableWorkers: string[]) => {
      setLoadingUpdate(true);
      await updateAuditorPermissions(id, addWorkers, removeWorkers, disableWorkers);
      setLoadingUpdate(false);
      setOpenWorkerModal(false);
      setOpenFiltersModal(false);
      setNeedsFetch(true);
    },
    [setNeedsFetch],
  );

  const updateFilters = async (
    id: string,
    scoreFilters: GlobalWorkerSkillScorePermission[],
    requiredTags: { label: string; value: string }[],
    excludedTags: { label: string; value: string }[],
  ) => {
    await API.genai.updatePermissionFilters({
      id,
      globalWorkerSkillScorePermissions: scoreFilters,
      globalRequiredTagLabelsValues: requiredTags,
      globalExcludedTagLabelsValues: excludedTags,
    });
  };

  const handleUpdateFilters = useCallback(
    async (
      id: string,
      scoreFilters: GlobalWorkerSkillScorePermission[],
      requiredTags: { label: string; value: string }[],
      excludedTags: { label: string; value: string }[],
    ) => {
      setLoadingUpdate(true);
      await updateFilters(id, scoreFilters, requiredTags, excludedTags);
      setLoadingUpdate(false);
      setOpenFiltersModal(false);
      setNeedsFetch(true);
    },
    [setNeedsFetch],
  );

  const handleEditRole = useCallback(
    async (roleToUpdate: AuditPermissionRole) => {
      setLoadingUpdate(true);
      await API.genai.updateAuditPermissionRole({
        id: roleToUpdate.id,
        name: roleToUpdate.name,
        description: roleToUpdate.description,
        courseIds: roleToUpdate.courseIds,
        sectionTitles: roleToUpdate.sectionTitles,
      });
      setLoadingUpdate(false);
      setOpenEditModal(false);
      setNeedsFetch(true);
    },
    [setNeedsFetch],
  );

  const handleForkRole = useCallback(
    async (roleToFork: AuditPermissionRole) => {
      setLoadingUpdate(true);
      await API.genai.publishAuditPermissionRole({
        name: roleToFork.name,
        description: roleToFork.description,
        courseIds: roleToFork.courseIds,
        sectionTitles: roleToFork.sectionTitles,
        globalWorkerSkillScorePermissions: roleToFork.globalWorkerSkillScorePermissions,
        globalRequiredTagLabelsValues: roleToFork.globalRequiredTagLabelsValues,
        globalExcludedTagLabelsValues: roleToFork.globalExcludedTagLabelsValues,
      });
      setLoadingUpdate(false);
      setOpenForkModal(false);
      setNeedsFetch(true);
    },
    [setNeedsFetch],
  );

  const handleArchiveRole = useCallback(
    async (roleToUpdate: AuditPermissionRole) => {
      setLoadingUpdate(true);
      await API.genai.archiveAuditPermissionRole({
        id: roleToUpdate.id,
      });
      setLoadingUpdate(false);
      setOpenArchiveModal(false);
      setNeedsFetch(true);
    },
    [setNeedsFetch],
  );

  const updateBenchmarkSettings = async (
    id: string,
    benchmarkWall: number,
    benchmarkAccuracyBar: number,
  ) => {
    await API.genai.updatePermissionBenchmarkSettings({
      id,
      benchmarkWall,
      benchmarkAccuracyBar,
    });
  };

  const handleUpdateBenchmarkSettings = useCallback(
    async (id: string, benchmarkWall: number, benchmarkAccuracyBar: number) => {
      setLoadingUpdate(true);
      await updateBenchmarkSettings(id, benchmarkWall, benchmarkAccuracyBar);
      setLoadingUpdate(false);
      setOpenBenchmarksModal(false);
      setNeedsFetch(true);
    },
    [setNeedsFetch],
  );

  useEffect(() => {
    if (needsFetch) {
      handleFetchPermissions();
    }
  }, [handleFetchPermissions, needsFetch]);

  const tableData = useMemo(() => {
    const data = permissions
      .filter(permission => !permission.archived)
      .map(permission => ({
        id: permission.id,
        name: permission.name,
        description: permission.description,
        edit: (
          <div className="flex flex-row">
            <Button
              variant="neutral"
              disabled={loadingUpdate}
              onClick={() => {
                setActivePermission(permission);
                setOpenEditModal(true);
              }}
            >
              Edit
            </Button>
            <Button
              className="ml-2"
              variant="neutral"
              disabled={loadingUpdate}
              onClick={() => {
                setActivePermission({ ...permission, name: `COPY OF ${permission.name}` });
                setOpenForkModal(true);
              }}
            >
              Clone
            </Button>
          </div>
        ),
        reviewAndAssign: (
          <Button
            variant="secondary"
            disabled={loadingUpdate}
            onClick={() => {
              setActivePermission(permission);
              setOpenWorkerModal(true);
            }}
          >
            Review and Assign Workers
          </Button>
        ),
        reviewFilters: (
          <Button
            variant="secondary"
            disabled={loadingUpdate}
            onClick={() => {
              setActivePermission(permission);
              setOpenFiltersModal(true);
            }}
          >
            Review Global Filters
          </Button>
        ),
        reviewBenchmarks: (
          <Button
            variant="secondary"
            disabled={loadingUpdate}
            onClick={() => {
              setActivePermission(permission);
              setOpenBenchmarksModal(true);
            }}
          >
            Review Benchmarks
          </Button>
        ),
        courseIds: permission.courseIds.join(', '),
        sectionTitles: permission.sectionTitles.join(', '),
        createdBy: <SmartLink type={INamedObjectTypes.User} id={permission.createdBy} />,
        numWorkersPermissioned: permission.workersPermissioned?.length ?? 0,
        numBenchmarksAssigned: permission.benchmarksAssigned?.length ?? 0,
      }));
    return data;
  }, [loadingUpdate, permissions]);

  const columns = useMemo(() => {
    return Object.keys(tableHeaders).map(label => ({
      Header: tableHeaders[label],
      accessor: label,
      show: shownTableHeaders.includes(label),
      filterable: filterableTableHeaders.includes(label),
      ...(smallColumns.includes(label) && { minWidth: 60 }),
    }));
  }, []);

  return (
    <div>
      {loadingTable ? (
        <CircularProgress />
      ) : (
        <div>
          <Table
            filterable
            data={tableData}
            columns={columns}
            defaultFilterMethod={(filter, row) => {
              const rowValue =
                filter.id === 'createdBy' ? `${row[filter.id].props.id}` : row[filter.id];
              return (rowValue ?? '').toLowerCase().includes(filter.value.toLowerCase());
            }}
          />
          {activePermission && (
            <div>
              <TaskerScreeningAuditorPermissionReviewModal
                role={activePermission}
                open={openWorkerModal}
                onClose={() => setOpenWorkerModal(false)}
                onSave={handleUpdateAuditorPermissions}
              />
              <TaskerScreeningAuditorPermissionFilterModal
                role={activePermission}
                open={openFiltersModal}
                onClose={() => setOpenFiltersModal(false)}
                onSave={handleUpdateFilters}
              />
              <TaskerScreeningAuditorPermissionBenchmarkModal
                role={activePermission}
                open={openBenchmarksModal}
                onClose={() => setOpenBenchmarksModal(false)}
                onSave={handleUpdateBenchmarkSettings}
              />
              <TaskerScreeningAuditPermissionRoleCreationModal
                role={activePermission}
                setRole={setActivePermission}
                open={openEditModal || openForkModal}
                onClose={() => {
                  setOpenEditModal(false);
                  setOpenForkModal(false);
                }}
                onPublish={isArchive => {
                  if (isArchive) {
                    setOpenEditModal(false);
                    setOpenArchiveModal(true);
                  } else if (openEditModal) {
                    handleEditRole(activePermission);
                  } else {
                    handleForkRole(activePermission);
                  }
                }}
                canArchive={openEditModal}
                buttonText={openEditModal ? 'Update Role' : 'Clone Role'}
              />
              <TaskerScreeningAuditPermissionRoleArchiveModal
                open={openArchiveModal}
                onClose={() => setOpenArchiveModal(false)}
                onPublish={() => {
                  handleArchiveRole(activePermission);
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskerScreeningAuditorPermissionsTool;
